import { useTranslation } from 'react-i18next';
import type { SextantKey, PSRScore } from '@/utils/perio';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PsrSextantGridProps {
  scores: Record<SextantKey, PSRScore>;
  /** Highlight these sextant keys (e.g. on click from grid) */
  highlighted?: SextantKey | null;
  onSextantClick?: (key: SextantKey) => void;
}

// ── Score color mapping ────────────────────────────────────────────────────────

function scoreColor(score: PSRScore): string {
  if (score === null) return 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-700 dark:text-gray-500 dark:border-gray-600';
  if (score === 0)    return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700';
  if (score === 1)    return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700';
  if (score === 2)    return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700';
  if (score === 3)    return 'bg-orange-200 text-orange-900 border-orange-400 dark:bg-orange-800/40 dark:text-orange-200 dark:border-orange-600';
  return              'bg-red-200 text-red-900 border-red-400 dark:bg-red-900/40 dark:text-red-200 dark:border-red-600'; // score 4
}

// ── Layout: upper row = s1, s2, s3 / lower row = s6, s5, s4 ──────────────────

const SEXTANT_LAYOUT: [SextantKey, SextantKey, SextantKey][] = [
  ['s1', 's2', 's3'],
  ['s6', 's5', 's4'],
];

const SEXTANT_LABELS: Record<SextantKey, string> = {
  s1: 'S1 (17-14)',
  s2: 'S2 (13-23)',
  s3: 'S3 (24-27)',
  s4: 'S4 (47-44)',
  s5: 'S5 (43-33)',
  s6: 'S6 (34-37)',
};

// ── Component ─────────────────────────────────────────────────────────────────

export function PsrSextantGrid({
  scores,
  highlighted,
  onSextantClick,
}: PsrSextantGridProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        PSR
      </p>
      <div className="space-y-1">
        {SEXTANT_LAYOUT.map((row, rowIdx) => (
          <div key={rowIdx} className="grid grid-cols-3 gap-1">
            {row.map((key) => {
              const score = scores[key];
              const isHighlighted = highlighted === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSextantClick?.(key)}
                  title={SEXTANT_LABELS[key]}
                  className={`
                    flex flex-col items-center justify-center rounded border px-2 py-2 text-center transition-all
                    ${scoreColor(score)}
                    ${isHighlighted ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}
                    ${onSextantClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
                  `}
                >
                  <span className="text-xs font-bold">{key.toUpperCase()}</span>
                  <span className="mt-0.5 text-base font-extrabold leading-none">
                    {score === null
                      ? t('dental.perio.psr.noData', '–')
                      : t('dental.perio.psr.score', { n: score, defaultValue: String(score) })}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
